import { classed, clsx, type VariantProps } from "@trydig/ui";
import type { ComponentProps } from "react";
import { Spinner } from "@/components/icons/Spinner";
import { Link } from "@/i18n/routing";
import styles from "./Button.module.css";

const ButtonBase = classed.button(clsx(styles.root, "button"), {
	variants: {
		variant: {
			default: "",
			filter: styles.filterButton,
			community: styles.communityButton,
			control: styles.controlButton,
			radio: styles.radioButton,
		},
		loading: {
			true: styles.loading,
		},
		color: {
			coral: styles.coral,
			purple: styles.purple,
		},
		selected: {
			true: styles.selected,
		},
	},
	defaultVariants: {
		variant: "default",
		color: "coral",
	},
});

const ButtonLink = classed(Link, ButtonBase, "button");

export interface ButtonProps extends ComponentProps<typeof ButtonBase> {
	href?: string;
	fullWidth?: boolean;
}

export const Button = ({
	children,
	className,
	fullWidth,
	loading,
	href,
	...props
}: ButtonProps) => {
	const inner = (
		<>
			<span className={clsx(styles.inner, loading && styles.loading)}>
				{children}
			</span>
			{loading && (
				<span className={styles.loadingSpinner}>
					<Spinner />
				</span>
			)}
		</>
	);

	if (href) {
		return (
			<ButtonLink
				className={clsx(fullWidth && styles.fullWidth, className)}
				{...(props as ComponentProps<typeof Link> &
					VariantProps<typeof ButtonBase>)}
				loading={loading}
				href={href}
			>
				{inner}
			</ButtonLink>
		);
	}

	return (
		<ButtonBase
			className={clsx(fullWidth && styles.fullWidth, className)}
			loading={loading}
			{...props}
		>
			{inner}
		</ButtonBase>
	);
};
